import React, { useState } from 'react';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  PasswordInput,
  BooleanInput,
  required,
  useTranslate,
  useNotify,
  useRedirect,
  SaveButton,
  Toolbar,
  FormDataConsumer,
} from 'react-admin';
import { Box, Typography, Alert, Button, CircularProgress, Card, CardContent, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { CloudQueue, CheckCircle, ArrowBack, VpnKey, Settings } from '@mui/icons-material';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

// The form uses snake_case sources (e.g. `supabase_url`) but the backend Zod
// schema validates camelCase fields (e.g. `supabaseUrl`). Map camelCase → snake
// so server errors land on the correct form inputs.
const SUPABASE_CAMEL_TO_FORM: Record<string, string> = {
    supabaseUrl: 'supabase_url',
    serviceKey: 'service_key',
    databaseUrl: 'database_url',
    isActive: 'is_active',
};

// The i18n file uses camelCase field keys; map the form's snake_case source
// back so the label lookup resolves.
const SUPABASE_FORM_TO_LABEL: Record<string, string> = {
    supabase_url: 'supabaseUrl',
    service_key: 'serviceKey',
    database_url: 'databaseUrl',
    is_active: 'isActive',
};

const SUPABASE_CONNECTION_KNOWN_FIELDS = new Set([
    'name', 'description', 'supabase_url', 'service_key',
    'database_url', 'environment', 'is_active',
]);

const mapSupabaseConnectionBackendPath = (path: string): string => {
    const stripped = path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');
    return SUPABASE_CAMEL_TO_FORM[stripped] || stripped;
};

const supabaseConnectionFieldLabelKey = (field: string): string => {
    if (!SUPABASE_CONNECTION_KNOWN_FIELDS.has(field)) return field;
    const labelKey = SUPABASE_FORM_TO_LABEL[field] || field;
    return `resources.supabase-connections.fields.${labelKey}`;
};

const CreateToolbar = () => {
  const translate = useTranslate();

  return (
    <Toolbar
      sx={{
        backgroundColor: '#FAFAFA',
        borderTop: '1px solid #E0E0E0',
        px: { xs: 2, sm: 3 },
        py: 2,
        mt: { xs: 2, sm: 4 },
        '& .MuiButton-root': {
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          py: 1.5,
        },
      }}
    >
      <SaveButton
        variant="contained"
        sx={{
          backgroundColor: '#00C853',
          '&:hover': {
            backgroundColor: '#00A843',
            boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
          },
        }}
      />
    </Toolbar>
  );
};

const PageHeader = () => {
  const redirect = useRedirect();
  const translate = useTranslate();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
      <IconButton
        onClick={() => redirect('/supabase-connections')}
        size="large"
        sx={{
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          '&:hover': {
            backgroundColor: '#F5F5F5',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          },
        }}
        title={translate('ra.action.back')}
      >
        <ArrowBack />
      </IconButton>
      <Box>
        <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
          {translate('resources.supabase-connections.create.title')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {translate('resources.supabase-connections.create.subtitle')}
        </Typography>
      </Box>
    </Box>
  );
};

const TestConnectionButton = ({ formData }: { formData: any }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState<{ success: boolean; message: string } | null>(null);

  const handleTestConnection = async () => {
    console.log('Form data:', formData);

    if (!formData.supabase_url || !formData.service_key) {
      console.log('Missing credentials:', {
        supabase_url: formData.supabase_url,
        service_key: formData.service_key,
      });
      notify('Please fill in all required credentials', { type: 'warning' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      // Get the access token from tokenManager
      const accessToken = await tokenManager.getAccessToken();
      if (!accessToken) {
        notify('Authentication required. Please log in again.', { type: 'error' });
        setTesting(false);
        return;
      }

      const response = await fetch(`${API_CONFIG.baseURL}/api/backend/supabase-connections/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          supabase_url: formData.supabase_url,
          service_key: formData.service_key,
          database_url: formData.database_url,
        }),
      });

      const result = await response.json();

      if (result.success) {
        setTestResult({ success: true, message: 'Connection successful!' });
        notify('Connection test successful', { type: 'success' });
      } else {
        setTestResult({ success: false, message: result.error || 'Connection failed' });
        notify(`Connection test failed: ${result.error}`, { type: 'error' });
      }
    } catch (error: any) {
      setTestResult({ success: false, message: error.message || 'Unknown error' });
      notify(`Connection test error: ${error.message || 'Unknown error'}`, { type: 'error' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2 }}>
      <Button
        variant="outlined"
        onClick={handleTestConnection}
        disabled={testing}
        startIcon={testing ? <CircularProgress size={16} /> : <CheckCircle />}
      >
        {testing
          ? translate('resources.supabase-connections.actions.testing')
          : translate('resources.supabase-connections.actions.testConnection')}
      </Button>

      {testResult && (
        <Alert
          severity={testResult.success ? 'success' : 'error'}
          sx={{ flex: 1 }}
        >
          {testResult.message}
        </Alert>
      )}
    </Box>
  );
};

export const SupabaseConnectionsCreate = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();
  const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

  return (
    <Create
      title={false}
      component="div"
      redirect="list"
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          setServerErrors({});
          notify('ra.notification.created', { type: 'success', messageArgs: { smart_count: 1 } });
          redirect('list', 'supabase-connections');
        },
        onError: (error: any) => {
          const apiError = error?.body || error;
          const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
          const fieldErrors: Record<string, string> = {};
          const summaryLines: string[] = [];
          if (rawErrors) {
            for (const [path, msgs] of Object.entries(rawErrors)) {
              const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
              const translated = translateServerMessage(rawMsg, translate);
              const field = mapSupabaseConnectionBackendPath(path);
              fieldErrors[field] = translated;
              const label = translate(supabaseConnectionFieldLabelKey(field), { _: field });
              summaryLines.push(`• ${label}: ${translated}`);
            }
          }
          setServerErrors(fieldErrors);

          const headerKey = apiError?.errorCode
            ? `errors.${apiError.errorCode}`
            : 'validation.formHasErrors';
          const header = translate(headerKey, { _: apiError?.message || 'Error' });

          if (summaryLines.length > 0) {
            notify(`${header}\n${summaryLines.join('\n')}`, {
              type: 'error',
              multiLine: true,
              autoHideDuration: 10000,
            });
          } else {
            notify(header, { type: 'error', autoHideDuration: 6000 });
          }
        },
      }}
      sx={{
        backgroundColor: 'transparent',
        '& .RaCreate-main': {
          backgroundColor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <SimpleForm
        toolbar={<CreateToolbar />}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
          '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
            px: { xs: 0, sm: 2 },
            py: { xs: 1, sm: 2 },
          },
          padding: { xs: 0, sm: 'inherit' },
        }}
      >
        <ServerErrorBinder errors={serverErrors} />
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' }, pb: { xs: 10, sm: 2 } }}>
          <PageHeader />

          {/* Basic Information Section */}
          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <CloudQueue sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.supabase-connections.sections.basic')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  source="name"
                  label={translate('resources.supabase-connections.fields.name')}
                  validate={required()}
                  fullWidth
                  helperText={translate('resources.supabase-connections.helpers.name')}
                />

                <SelectInput
                  source="environment"
                  label={translate('resources.supabase-connections.fields.environment')}
                  choices={[
                    { id: 'production', name: 'Production' },
                    { id: 'staging', name: 'Staging' },
                    { id: 'development', name: 'Development' },
                    { id: 'uat', name: 'UAT' },
                  ]}
                  validate={required()}
                  fullWidth
                  helperText={translate('resources.supabase-connections.helpers.environment')}
                />

                <TextInput
                  source="description"
                  label={translate('resources.supabase-connections.fields.description')}
                  fullWidth
                  multiline
                  rows={2}
                  helperText={translate('resources.supabase-connections.helpers.description')}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Credentials Section */}
          <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <VpnKey sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.supabase-connections.sections.credentials')}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                {translate('resources.supabase-connections.info.credentials')}
              </Alert>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  source="supabase_url"
                  label={translate('resources.supabase-connections.fields.supabaseUrl')}
                  validate={required()}
                  fullWidth
                  placeholder="https://xxxxxxxxxxxx.supabase.co"
                  helperText={translate('resources.supabase-connections.helpers.supabaseUrl')}
                />

                <PasswordInput
                  source="service_key"
                  label={translate('resources.supabase-connections.fields.serviceKey')}
                  validate={required()}
                  fullWidth
                  helperText={translate('resources.supabase-connections.helpers.serviceKey')}
                />


                <PasswordInput
                  source="database_url"
                  label={translate('resources.supabase-connections.fields.databaseUrl')}
                  fullWidth
                  placeholder="postgresql://postgres:password@db.xxxxxxxxxxxx.supabase.co:5432/postgres"
                  helperText={translate('resources.supabase-connections.helpers.databaseUrl')}
                />

                <FormDataConsumer>
                  {({ formData }) => <TestConnectionButton formData={formData} />}
                </FormDataConsumer>
              </Box>
            </CardContent>
          </Card>

          {/* Settings Section */}
          <Card sx={{ overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Settings sx={{ mr: 2, color: '#00C853' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {translate('resources.supabase-connections.sections.settings')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <BooleanInput
                  source="is_active"
                  label={translate('resources.supabase-connections.fields.isActive')}
                  defaultValue={true}
                  helperText={translate('resources.supabase-connections.helpers.isActive')}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </SimpleForm>
    </Create>
  );
};